import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMap } from "../../../../context/map";
import { useRoot } from "../../../../context/root";
import { useFetchCity } from "../../../../hooks/useFetchCity";
import { useFetchState } from "../../../../hooks/useFetchState";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import {
  fetchBusinessCategory,
  fetchSpeciality,
  generatePseudorandomId,
  initialValues 
} from "./utils";

export const useFilters = (setOpen: (value: boolean) => void) => {
  const navigator = useNavigate();
  const location = useLocation();
  const { geohash } = useParams();
  const { rootDispatch } = useRoot();
  const { mapContext } = useMap();
  const formikRef = useRef<any>(null);
  const { searchStateValue, setSearchStateValue, states } = useFetchState();
  const { cities, setSearchCityValue, searchCityValue } = useFetchCity();
  const { isMobile } = useIsMobile();
  const searchParams = new URLSearchParams(location.search);
  const [searchValue, setSearchValue] = useState("");
  const [specialitiesOptions, setSpecialitiesOptions] = useState<any>([]);
  const [businessCategorySearchName, setBusinessCategorySearchName] = useState("");
  const [businessOptions, setBusinessOptions] = useState<any>([]);
  const [activeTab, setActiveTab] = useState(() => {
    const itemType = searchParams.get("item_type");
    if (itemType) {
      return itemType
    }
    return "all"
  })

  useEffect(() => {
    if (activeTab === "physician") {
      fetchSpeciality(searchValue).then((res) => {
        setSpecialitiesOptions(res)
      })
    }
  }, [searchValue, activeTab])

  useEffect(() => {
    if (activeTab === "Business") {
      fetchBusinessCategory(businessCategorySearchName).then((res) => {
        setBusinessOptions(res);
      })
    }
  }, [businessCategorySearchName, activeTab])

  const handleCategoryChange = (category: any) => {
    setActiveTab(category.value);
    formikRef.current.resetForm({ values: initialValues })
  }

  const handleSubmit = (values: any) => {
    rootDispatch({
      type: "setIsMapMoving",
      data: false
    })
    rootDispatch({
      type: "setDataList",
      data: []
    });
    rootDispatch({
      type: "setDataMarkers",
      data: []
    });
    rootDispatch({
      type: "setExtraMarker",
      data: null,
      mapContext,
    });
    Object.keys(values).map((key) => {
      if (values[key]) {
        if (typeof values[key] === "boolean") {
          searchParams.set(key, "true");
        }
        if (typeof values[key] === "string" || typeof values[key] === "number") {
          searchParams.set(key, values[key])
        }
        if (['speciality', 'category', 'city', 'state'].includes(key)) {
          searchParams.set(key, values[key].value)
        } 
      }
      else {
        searchParams.delete(key)
      }
    })
    if (activeTab === "all") {
      searchParams.delete("item_type");
    }
    else {
      searchParams.set("item_type", activeTab)
    }
    if (geohash) {
      navigator({
        pathname: `${process.env.REACT_APP_INTERNAL_URL}bucket/${geohash}`,
        search: searchParams.toString()
      })
    }
    else {
      navigator({
        pathname: `${process.env.REACT_APP_INTERNAL_URL}`,
        search: searchParams.toString()
      })
    }
    rootDispatch({
      type: "setRefetch",
      data: {
        shouldRefetch: true,
        geohash: geohash || null
      }
    })
    setOpen(false)
  }

  const clearSearchParams = () => {
    Object.keys(initialValues).map((key) => {
      searchParams.delete(key)
    })
    rootDispatch({
      type: "setIsMapMoving",
      data: false
    })
    rootDispatch({
      type: "setDataList",
      data: []
    });
    rootDispatch({
      type: "setDataMarkers",
      data: []
    });
    rootDispatch({
      type: "setExtraMarker",
      data: null,
      mapContext
    });
    if (geohash) {
      navigator({
        pathname: `${process.env.REACT_APP_INTERNAL_URL}bucket/${geohash}`,
        search: searchParams.toString()
      })
    }
    else {
      navigator({
        pathname: `${process.env.REACT_APP_INTERNAL_URL}`,
        search: searchParams.toString()
      })
    }
    rootDispatch({
      type: "setRefetch",
      data: {
        shouldRefetch: true,
        geohash: geohash || null
      }
    })
    setOpen(false);
  }

  return {
    handleSubmit,
    formikRef,
    activeTab,
    savedFilters: {
      ...initialValues,
      ...Object.keys(Object.fromEntries(searchParams)).reduce((acc: any, cur: any) => {
        if (["is_recommended", "is_premium", "is_claimed", "is_board_certified"].includes(cur)) {
          return {
            ...acc,
            [cur]: true
          }
        }
        if (['speciality', 'category', 'city', 'state'].includes(cur)) {
          return {
            ...acc,
            [cur]: {
              id: generatePseudorandomId(16),
              name: Object.fromEntries(searchParams)[cur],
              value: Object.fromEntries(searchParams)[cur]
            }
          }
        }
        if (['rating_from'].includes(cur)) {
          return {
            ...acc,
            [cur]: Number(Object.fromEntries(searchParams)[cur])
          }
        }
        return {
          ...acc,
          [cur]: Object.fromEntries(searchParams)[cur]
        }
      }, {})
    },
    clearSearchParams,
    searchValue,
    setSearchValue,
    specialitiesOptions,
    isMobile,
    handleCategoryChange,
    businessOptions,
    businessCategorySearchName,
    setBusinessCategorySearchName,
    claimActive: !!searchParams.get("claim"),
    searchStateValue,
    setSearchStateValue,
    states,
    cities,
    setSearchCityValue,
    searchCityValue
  }
}
